import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, Send, Settings2 } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function PaymentReminders() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [dialog, setDialog] = useState(null);   // { row, channel, force, note }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conf, setConf] = useState({ auto_mode: "off", signature: "" });
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    try {
      const [o, l] = await Promise.all([
        api.get("/reminders/overdue"),
        api.get("/reminders/log?limit=40").catch(() => ({ data: [] })),
      ]);
      setData(o.data);
      setConf(o.data.settings);
      setLog(l.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const report = (res) => {
    const ok = res.delivered?.length;
    if (ok) toast.success(`Reminder sent by ${res.delivered.join(" & ")}`);
    else toast.info("Reminder logged — send it on WhatsApp with the link below");
    if (res.whatsapp?.wa_link) {
      window.open(res.whatsapp.wa_link, "_blank", "noopener");
    }
    if (res.email?.status === "no_address")
      toast.warning("This customer has no email saved");
  };

  const send = async () => {
    const { row, channel, force, note } = dialog;
    setBusy(row.invoice_id);
    try {
      const { data: res } = await api.post(`/reminders/invoice/${row.invoice_id}/send`,
        { channel, force, note });
      report(res);
      setDialog(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const remindAll = async () => {
    setBusy("all");
    try {
      const { data: res } = await api.post("/reminders/run-all", { channel: "email" });
      toast.success(`${res.sent_count} customer(s) reminded by email`);
      if (res.skipped?.length)
        toast.info(`${res.skipped.length} skipped (recently reminded or settled)`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const saveSettings = async () => {
    try {
      const { data: res } = await api.put("/reminders/settings", conf);
      setConf(res);
      setSettingsOpen(false);
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div>
      <PageHeader
        testId="reminders-header"
        title="Payment reminders"
        subtitle={`${inr(data.total_outstanding)} overdue across ${data.invoices.length} invoice(s)`}
        actions={
          <>
            <Button variant="outline" className="gap-1.5" data-testid="reminder-settings-button"
              onClick={() => setSettingsOpen(true)}>
              <Settings2 className="h-4 w-4" /> Settings
            </Button>
            {can("payments", "edit") && data.invoices.length > 0 && (
              <Button className="gap-1.5" data-testid="remind-all-button"
                disabled={busy === "all"} onClick={remindAll}>
                {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />}
                Remind all by email
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap gap-2 border-b border-border px-4 py-3 sm:px-6"
        data-testid="reminder-channel-status">
        <Badge variant={data.email_ready ? "secondary" : "outline"}>
          Email {data.email_ready ? "ready" : "not configured"}
        </Badge>
        <Badge variant={data.whatsapp_ready ? "secondary" : "outline"}>
          WhatsApp {data.whatsapp_ready ? "ready" : "opens on your phone / web"}
        </Badge>
        <Badge variant="outline">
          Auto reminders: {conf.auto_mode === "off" ? "off (you press the button)"
            : conf.auto_mode === "daily" ? "daily 10 am" : "every Monday"}
        </Badge>
      </div>

      <div className="p-4 sm:p-6">
        {data.invoices.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground" data-testid="reminders-empty">
            Nothing is overdue right now — every invoice is within its due date.
          </p>
        ) : (
          <div className="surface overflow-x-auto" data-testid="overdue-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Due date</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Reminders</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invoices.map((r) => (
                  <TableRow key={r.invoice_id} data-testid={`overdue-row-${r.invoice_id}`}>
                    <TableCell className="font-mono-num text-xs">{r.number}</TableCell>
                    <TableCell className="text-sm">
                      {r.customer_name}
                      <div className="text-[11px] text-muted-foreground">
                        {r.email || "no email"} · {r.whatsapp || "no mobile"}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {shortDate(r.due_date)}
                      <Badge variant="destructive" className="ml-1 text-[9px]">
                        {r.days_overdue}d
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">
                      {inr(r.outstanding)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.reminders_sent > 0
                        ? `${r.reminders_sent} · last ${shortDate(r.last_reminder_at)}`
                        : "none yet"}
                      {r.in_cooldown && (
                        <Badge variant="outline" className="ml-1 text-[9px]">waiting</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {can("payments", "edit") && (
                        <Button size="sm" variant="outline" className="gap-1"
                          data-testid={`send-reminder-${r.invoice_id}`}
                          disabled={busy === r.invoice_id}
                          onClick={() => setDialog({ row: r, channel: "both", force: false, note: "" })}>
                          <MessageCircle className="h-3.5 w-3.5" /> Send reminder
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {log.length > 0 && (
          <div className="surface mt-6 overflow-x-auto" data-testid="reminder-log">
            <div className="border-b border-border px-4 py-2.5 text-sm font-medium">
              Reminder history
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sent</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs">{shortDate(l.sent_at)}</TableCell>
                    <TableCell className="font-mono-num text-xs">{l.invoice_number}</TableCell>
                    <TableCell className="text-sm">{l.customer_name}</TableCell>
                    <TableCell className="text-xs">
                      email: {l.email_status} · whatsapp: {l.whatsapp_status}
                    </TableCell>
                    <TableCell className="text-xs">{l.sent_by_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle>
              Remind {dialog?.row.customer_name} about {dialog?.row.number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={dialog?.channel}
              onValueChange={(v) => setDialog((d) => ({ ...d, channel: v }))}>
              <SelectTrigger data-testid="reminder-channel-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Email + WhatsApp</SelectItem>
                <SelectItem value="email">Email only</SelectItem>
                <SelectItem value="whatsapp">WhatsApp only</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Anything you want to add (optional)"
              data-testid="reminder-note-input" value={dialog?.note || ""}
              onChange={(e) => setDialog((d) => ({ ...d, note: e.target.value }))} />
            {dialog?.row.in_cooldown && (
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={dialog?.force} data-testid="reminder-force-checkbox"
                  onCheckedChange={(v) => setDialog((d) => ({ ...d, force: !!v }))} />
                A reminder went out recently — send anyway
              </label>
            )}
            <p className="text-[11px] text-muted-foreground">
              {data.whatsapp_ready
                ? "WhatsApp will be delivered automatically."
                : "WhatsApp isn't connected yet, so we'll open the ready-made message for you to press send."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={send} data-testid="reminder-send-confirm"
              disabled={busy === dialog?.row.invoice_id}>
              {busy === dialog?.row.invoice_id && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent data-testid="reminder-settings-dialog">
          <DialogHeader><DialogTitle>Automatic reminders</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={conf.auto_mode}
              onValueChange={(v) => setConf((c) => ({ ...c, auto_mode: v }))}>
              <SelectTrigger data-testid="auto-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off — I'll press the button myself</SelectItem>
                <SelectItem value="daily">Every morning at 10 am</SelectItem>
                <SelectItem value="weekly">Every Monday morning</SelectItem>
              </SelectContent>
            </Select>
            <Textarea data-testid="reminder-signature-input" value={conf.signature || ""}
              onChange={(e) => setConf((c) => ({ ...c, signature: e.target.value }))}
              placeholder="Signature at the end of the message" />
            <p className="text-[11px] text-muted-foreground">
              Automatic reminders go out by email only, at 1, 7, 15 and 30 days past the due date,
              and never twice within 3 days.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} data-testid="reminder-settings-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
